import { checkType, CtyButton, CtyShowToast } from "@circuitry-ai/doc-ui";
import { Divider, Input, Link, Select, SelectItem } from "@nextui-org/react";
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetIntegrationsMutation, useUpdateAiWorkersMutation, useUploadInternalActionMutation, useEntityResourcePermissions } from '@circuitry-ai/doc-data';
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Icon } from "@iconify/react";

/* eslint-disable-next-line */
export interface AnalystnotificationsProps { }

export function Analystnotifications(props: AnalystnotificationsProps) {

  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const { report } = location?.state ?? {};

  const [searchParams] = useSearchParams();
  const aiworkerId = searchParams.get('aiworkerId');
  const { canUpdate } = useEntityResourcePermissions('analyst', aiworkerId ?? undefined);

  const [email, setEmail] = useState<string>('');
  const [emailNotificationOption, setEmailNotificationOption] = useState<string>('');
  const [emailList, setEmailList] = useState<string[]>([]);
  const [adfStatus, setAdfStatus] = useState<string>('');

  const [updateAiWorkers, { isLoading: isUpdataingAiworkerData }] = useUpdateAiWorkersMutation();
  const [getIntegrations] = useGetIntegrationsMutation();
  const [uploadInternalAction] = useUploadInternalActionMutation();

  useEffect(() => {
    const keyed = report.aiworkers_attrs.reduce((acc, item) => {
      acc[item.attr_name] = item;
      return acc;
    }, {});
    console.log('keyed', keyed);
    setEmailList(keyed?.emails?.attr_json?.emails ?? [] );
    setEmailNotificationOption(keyed?.frequency?.attr_value ?? '');
    setAdfStatus(keyed?.adf_status?.attr_value ?? '');
  }, [report]);

  const emailNotification = [
    { key: 'daily', title: t('Daily') },
    { key: 'weekly', title: t('Weekly') },
    { key: 'monthly', title: t('Monthly') },
  ];

  const updateEmailNotification = async () => {
    try {
      const payLoad = {
        aiworkers_attrs: [
          {
            attr_name: 'frequency',
            attr_value: emailNotificationOption,
            attr_json: null,
          },
          {
            attr_name: 'emails',
            attr_value: null,
            attr_json: { emails: emailList },
          }
        ]
      };
      const response = await updateAiWorkers({ payLoad, aiworker_id: aiworkerId });
      navigate(`${location.pathname}${location.search}`, { state: { report: response?.data?.data } });
      CtyShowToast(checkType.success, response?.data?.message);
    } catch (e) {
      CtyShowToast(checkType.error, (e as Error)?.message ?? 'Operation failed');
    }
  };

  const handleAddEmail = () => {
    if (email) {
      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      const isDuplicate = emailList.includes(email.trim().toLowerCase());
      if (isDuplicate) {
        CtyShowToast(checkType.error, 'Email already exists in the list');
      } else if (emailRegex.test(email) && !isDuplicate) {
        setEmailList([...emailList, email.trim().toLowerCase()]);
        setEmail('');
      }
    }
  };

  const handleMailUnsubscribe = async () => {
    try {
      const payLoad = {
        "name": "stop_adf_trigger",
        "payload": {
          "aiworker_id": aiworkerId
        }
      }
      const response = await uploadInternalAction(payLoad).unwrap();
      setAdfStatus('stopped');
      CtyShowToast(checkType.success, response?.message);
    } catch (e) {
      CtyShowToast(checkType.error, (e as Error)?.message ?? 'Operation failed');
    }
  }

  return (
    <div className="flex flex-col h-full gap-3 py-2 lg:w-[70%] md:w-[80%] w-full">
      <div className='w-full  '>
        <label htmlFor={'frequency'}>
          <span className='text-[#414651] font-[Poppins] font-medium text-sm leading-[20px] tracking-normal'>{t(`Notification Frequency`)}<span className="text-danger">*</span></span>
        </label>
        <Select
          labelPlacement='outside'
          disallowEmptySelection
          selectedKeys={new Set([emailNotificationOption])}
          placeholder="select a option"
          onSelectionChange={(keys) => {
            const userOption = [...keys][0] ?? 'daily';
            setEmailNotificationOption(userOption as string);
          }}
          aria-label="email-notification-selection"
          id="email-notification-selection"
        >
          {emailNotification?.map((option: any) => (
            <SelectItem key={option?.key} value={option.key}>
              {option.title}
            </SelectItem>
          ))}
        </Select>
      </div>
      <div className='w-full '>
        <label className='text-sm' htmlFor="email-input">Email<span className="text-danger">*</span></label>
        <div className="flex flex-row items-top gap-1">
          <Input
            id="email-input"
            size="sm"
            type="email"
            value={email}
            onValueChange={setEmail}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAddEmail();
              }
            }}
          />
          <CtyButton isIconOnly radius="sm" size="sm" className="border-1 border-default-300 m-0 p-0 h-[32px]" variant="bordered" onPress={handleAddEmail} isDisabled={!canUpdate}>
            <Icon icon="icons8:plus" width="1rem" height="1rem" style={{ color: "#737d8d" }} />
          </CtyButton>
        </div>
      </div>
      <div className={`w-full   ${emailList.length > 3 ? "max-h-[250px] overflow-y-auto" : ""}`}>
        {(emailList ?? [])?.map((email: string, index: number) => {
          return <div key={index + 'email'} className="flex items-center justify-between bg-default-100 pl-2 rounded-lg mb-2 border-1 font-normal text-sm">
            <span>{email}</span>
            <CtyButton
              variant='light'
              className="text-danger text-sm hover:text-danger"
              onClick={() => {
                const updatedEmailList = emailList.filter((_, i) => i !== index);
                setEmailList(updatedEmailList);
              }}
            >
              Remove
            </CtyButton>
          </div>;
        })}
      </div>
      <div className="flex flex-row justify-end w-full">
        <CtyButton variant="solid" color="primary" size="md" radius="sm" onPress={updateEmailNotification} isLoading={isUpdataingAiworkerData} isDisabled={!canUpdate || !emailNotificationOption || !emailList.length} >
           { adfStatus === 'started'  ? 'Update' : 'Subscribe' }
        </CtyButton>
      </div>
      <Divider />
      <Link onPress={handleMailUnsubscribe} className='cursor-pointer' isDisabled={!canUpdate || adfStatus === 'stopped' || !adfStatus}><span className="font-poppins font-semibold text-[14px] leading-[20px] tracking-[0px] text-primary mb-3">Unsubscribe mails</span></Link>

    </div>

  );
}

export default Analystnotifications;
